import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createBlock as _createBlock, renderList as _renderList, Fragment as _Fragment, withCtx as _withCtx, createVNode as _createVNode } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "loading-indicator"
}
const _hoisted_2 = { class: "text-align-center color-lighter" }
const _hoisted_3 = { key: 2 }

import AnimatedDots from '@/components/common/animated-dots/AnimatedDots.vue';
import BotMessageHeader from '@/components/common/bot-message-header/BotMessageHeader.vue';
import ChatInputComponent from '@/components/common/chat-input/ChatInputComponent.vue';
import ChatMessage from '@/components/common/chat-message/ChatMessage.vue';
import ChatSuggestions from '@/components/common/chat-suggestions/ChatSuggestions.vue';
import FloatingCard from '@/components/common/floating-card/FloatingCard.vue';
import { IndexIdentifiers } from '@/core/indexesEnum';
import { SourceDTO } from '@/dto/QuestionResponseDTO';
import {
  AssistantType,
  ChatAssistant,
  ReviewingAssistantType
} from '@/models/assistant-types';
import { SuggestionsCategory } from '@/models/chat.models';
import { Conversation } from '@/models/conversation';
import {
  BaseMessage,
  LegalAssistantMessage,
  LegalAssistantSectionsMessage
} from '@/models/messages';
import { useAuthStore } from '@/stores/authStore';
import { useUserSettingsStore } from '@/stores/userSettingsStore';
import { useToasterStore } from '@/stores/useToasterStore';
import { GlobalErrorHandler } from '@/utils/global-error.utils';
import { scrollToElementsBottom } from '@/utils/utils';
import ChatHistoryService from '@/services/ChatHistoryService';
import { AxiosError } from 'axios';
import {
  computed,
  nextTick,
  onMounted,
  onUnmounted,
  reactive,
  ref,
  watch,
  VNode
} from 'vue';
import { useChatStore } from '@/stores/chat-store';
import { useI18n } from 'vue-i18n';
import { getSectionPriority } from '@/core/sectionPriority';
import { isValidAnswer } from '@/utils/answer-validation.utils';

export interface ChatComponentProps {
  assistantType: ChatAssistant;
  suggestionsLoader?: () =>
    | Promise<SuggestionsCategory[]>
    | SuggestionsCategory[];
  allowDocumentTextSelection?: boolean;
  onSend: (
    text: string,
    conversation: Conversation
  ) => Promise<{ responseMessage: BaseMessage; pickle?: string }>;
  botMessageFooter?: (message: BaseMessage) => VNode | null;
  loadChatHistory?: boolean;
  useStreamingResponse?: boolean;
  selectedConversationId?: string | null;
}

const LINE_BREAK = '\n\n';
const AUTO_SCROLL_THRESHOLD = 20;

export default /*@__PURE__*/_defineComponent({
  __name: 'ChatComponent',
  props: {
    assistantType: {},
    suggestionsLoader: {},
    allowDocumentTextSelection: { type: Boolean, default: false },
    onSend: {},
    botMessageFooter: {},
    loadChatHistory: { type: Boolean, default: true },
    useStreamingResponse: { type: Boolean, default: false },
    selectedConversationId: { default: null }
  },
  setup(__props: any, { expose: __expose }) {

const props = __props;

const { t } = useI18n();
const toaster = useToasterStore();
const { setSelectedRagIndex, getSafeSelectedRagIndex } = useUserSettingsStore();
const chatStore = useChatStore();
const { products } = useAuthStore();

const chatInputValue = ref('');
const loadingSuggestions = ref<boolean>(false);
const unavailableIndex = ref<boolean>(false);
const messagesWrapper = ref<HTMLElement | null>(null);
let initPromise: Promise<void>;

const isNewConversation = ref(!props.selectedConversationId);
let conversation = reactive<Conversation>(
  new Conversation(props.assistantType, getSafeSelectedRagIndex())
);

const loadingConversation = ref(false);

watch(
  () => props.selectedConversationId,
  async (newId) => {
    loadingConversation.value = true;
    try {
      if (newId) {
        const restoreConversation =
          await ChatHistoryService.getConversationHistory(newId);
        if (restoreConversation) {
          const processedRestoreConversation =
            processConversationMessages(restoreConversation);
          Object.assign(conversation, processedRestoreConversation);
          isNewConversation.value = false;
        }
      } else {
        Object.assign(
          conversation,
          new Conversation(props.assistantType, getSafeSelectedRagIndex())
        );
        isNewConversation.value = true;
      }
    } catch (error) {
      console.error('Error loading conversation history:', error);
    } finally {
      loadingConversation.value = false; // Finalizar estado de carga
    }
  },
  { immediate: true }
);

const autoScrollEnabled = ref(true);
let lastScrollTop = 0;

const handleUserScroll = () => {
  if (!messagesWrapper.value) return;
  const { scrollTop, scrollHeight, clientHeight } = messagesWrapper.value;
  const distanceToBottom = scrollHeight - scrollTop - clientHeight;
  if (distanceToBottom <= AUTO_SCROLL_THRESHOLD) {
    autoScrollEnabled.value = true;
  } else if (scrollTop < lastScrollTop) {
    autoScrollEnabled.value = false;
  }
  lastScrollTop = scrollTop;
};

onMounted(async () => {
  initPromise = initComponent();
  nextTick(() => {
    if (messagesWrapper.value) {
      messagesWrapper.value.addEventListener('scroll', handleUserScroll);
    }
  });
});

onUnmounted(async () => {
  if (messagesWrapper.value) {
    messagesWrapper.value.removeEventListener('scroll', handleUserScroll);
  }
});

const initComponent = async () => {
  if (props.assistantType === AssistantType.LegalAssistant) {
    if (!conversation.ragIndex) {
      conversation.ragIndex = getSafeSelectedRagIndex();
    } else if (products.availableIndexes.includes(conversation.ragIndex!)) {
      setSelectedRagIndex(conversation.ragIndex);
    } else {
      unavailableIndex.value = true;
    }
  }

  if (!conversation.messages.length && !conversation.suggestions?.length) {
    await loadSuggestions();
  }

  await nextTick();
  scrollToElementsBottom(messagesWrapper.value);
};

watch(
  () => chatStore.loading,
  () => {
    scrollToElementsBottom(messagesWrapper.value);
  }
);

const botMessageTitle = computed(() => {
  switch (props.assistantType) {
    case AssistantType.LegalAssistant:
      return IndexIdentifiers[conversation.ragIndex!];
    case ReviewingAssistantType.DocumentReviewingAssistant:
      return t('reviewing.chat.bot_message_title');
    default:
      throw new Error(`Unsupported assistant type: ${props.assistantType}`);
  }
});

const loadSuggestions = async () => {
  loadingSuggestions.value = true;
  try {
    if (props.suggestionsLoader) {
      conversation.suggestions = await props.suggestionsLoader();
    }
  } catch (error) {
    console.error('Error loading suggestions:', error);
    await GlobalErrorHandler.handleError(error, toaster, t);
  } finally {
    loadingSuggestions.value = false;
  }
};

const handleSend = async (text: string) => {
  try {
    if (initPromise) await initPromise;

    conversation.addMessage(new BaseMessage(text, false));

    autoScrollEnabled.value = true;

    chatStore.setLoading(true);
    const response = await props.onSend(text, conversation);

    if (response && conversation.id && isNewConversation.value) {
      isNewConversation.value = false;
    }

    chatStore.setLoading(false);

    if (!props.useStreamingResponse) {
      conversation.addMessage(response.responseMessage);
    }
    conversation.pickle = response.pickle;
  } catch (error) {
    console.error('Error while sending question:', error);

    await GlobalErrorHandler.handleError(error, toaster, t);

    if (error instanceof AxiosError && error.response?.status === 401) {
      return;
    }
    conversation.addMessage(
      new BaseMessage(t('legal_assistant.error.response'), true)
    );
  } finally {
    chatStore.setLoading(false);
    chatInputValue.value = '';
  }
};

const resetConversation = async () => {
  try {
    chatStore.setLoading(true);

    conversation = reactive(
      new Conversation(props.assistantType, getSafeSelectedRagIndex())
    );
    await initComponent();
  } catch (error) {
    console.error('Error resetting conversation:', error);
  } finally {
    chatStore.setLoading(false);
  }
};

const lastMessageIsAnswer = (): boolean => {
  return (
    conversation.messages.length > 0 &&
    conversation.messages[conversation.messages.length - 1].isBot
  );
};

const getLastMessage = (): BaseMessage => {
  return conversation.messages[conversation.messages.length - 1];
};

const addBotMessage = () => {
  if (!lastMessageIsAnswer()) {
    conversation.addMessage(new LegalAssistantSectionsMessage('', true, null));
  }
};

const handleChunk = (chunk: string, section?: string) => {
  addBotMessage();
  if (lastMessageIsAnswer()) {
    const lastMsg = getLastMessage();

    if (section) {
      if (chunk !== '') {
        chatStore.setLoading(false);
      }
      lastMsg.text = `**${t(`legal_assistant.chat.sections.title.${section}`)}**${LINE_BREAK}${chunk}`;
      nextTick(() => {
        if (autoScrollEnabled.value) {
          scrollToElementsBottom(messagesWrapper.value);
        }
      });
      (lastMsg as LegalAssistantSectionsMessage).defaultSection = section;
    } else {
      lastMsg.text = chunk;
    }
  }
};

const handleSources = (sources?: SourceDTO[]) => {
  if (lastMessageIsAnswer()) {
    const lastMsg = getLastMessage();
    if (sources && sources.length > 0) {
      (lastMsg as LegalAssistantSectionsMessage).sources = sources;
    }
  }
};

const updateSectionResponse = (chunk: string, section: string) => {
  addBotMessage();
  if (lastMessageIsAnswer()) {
    const lastMsg = getLastMessage();
    if (lastMsg) {
      const legalAssistantSectionLastMsg =
        lastMsg as LegalAssistantSectionsMessage;
      if (!legalAssistantSectionLastMsg.sectionResponses) {
        legalAssistantSectionLastMsg.sectionResponses = {};
      }
      legalAssistantSectionLastMsg.sectionResponses[section] = chunk;
    }
  }
};

const updateSectionSources = (sources: SourceDTO[], section: string) => {
  addBotMessage();
  if (lastMessageIsAnswer()) {
    const lastMsg = getLastMessage();
    if (lastMsg) {
      const legalAssistantSectionLastMsg =
        lastMsg as LegalAssistantSectionsMessage;
      if (!legalAssistantSectionLastMsg.sectionSources) {
        legalAssistantSectionLastMsg.sectionSources = {};
      }
      legalAssistantSectionLastMsg.sectionSources[section] = sources;
    }
  }
};

function processConversationMessages(conversation: Conversation): Conversation {
  if (!conversation || !Array.isArray(conversation.messages))
    return conversation;

  // Get the productIndex from the conversation (default to 0 if not present)
  const productIndex =
    (conversation as Conversation & { productIndex?: number }).productIndex ??
    0;
  const sectionPriority = getSectionPriority(productIndex);
  let sectionPriorityIndex = 0;

  conversation.messages = conversation.messages.map(
    (msg: LegalAssistantMessage | LegalAssistantSectionsMessage) => {
      // Case 1: Already has text and sources
      if (msg.text && msg.sources) {
        return msg;
      }

      // Case 2: Needs to be built from sectionResponses/sectionSources
      if (
        (msg as LegalAssistantSectionsMessage).sectionResponses &&
        typeof (msg as LegalAssistantSectionsMessage).sectionResponses ===
          'object'
      ) {
        const sectionResponses =
          (msg as LegalAssistantSectionsMessage).sectionResponses ?? {};
        const sectionSources = (msg as LegalAssistantSectionsMessage)
          .sectionSources;

        // Start from the current sectionPriorityIndex and increment if not found
        let found = false;
        while (sectionPriorityIndex < sectionPriority.length && !found) {
          const section = sectionPriority[sectionPriorityIndex];
          if (
            typeof section === 'string' &&
            isValidAnswer(sectionResponses?.[section] as string)
          ) {
            const chunk = sectionResponses[section];
            const source = sectionSources?.[section] || [];
            msg.text = `**${t(`legal_assistant.chat.sections.title.${section}`)}**${LINE_BREAK}${chunk}`;
            msg.sources = source;
            (msg as LegalAssistantSectionsMessage).defaultSection = section;
            found = true;
          } else {
            sectionPriorityIndex++;
          }
        }
        if (!found) {
          // Fallback: no valid section found
          msg.text = '';
          msg.sources = [];
        }
      }
      return msg;
    }
  );
  return conversation;
}

__expose({
  resetConversation,
  handleSend,
  handleChunk,
  handleSources,
  updateSectionResponse,
  updateSectionSources
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    (loadingConversation.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
          _createElementVNode("p", _hoisted_2, _toDisplayString(_unref(t)('chat_history.loading')), 1)
        ]))
      : (_openBlock(), _createElementBlock("div", {
          key: 1,
          ref_key: "messagesWrapper",
          ref: messagesWrapper,
          class: "messages-wrapper"
        }, [
          (
        !_unref(conversation).suggestions?.length &&
        !_unref(conversation).messages.length &&
        !loadingSuggestions.value
      )
            ? (_openBlock(), _createBlock(ChatMessage, {
                key: 0,
                "hide-actions": true,
                message: {
        text: _unref(t)('legal_assistant.chat.welcome_message'),
        isBot: true
      },
                "bot-message-title": botMessageTitle.value,
                "bot-message-footer": props.botMessageFooter
              }, null, 8, ["message", "bot-message-title", "bot-message-footer"]))
            : _createCommentVNode("", true),
          (!loadingSuggestions.value && _unref(conversation).suggestions?.length)
            ? (_openBlock(), _createBlock(ChatSuggestions, {
                key: 1,
                "bot-message-title": botMessageTitle.value,
                "suggestion-categories": _unref(conversation).suggestions,
                "handle-suggestion-click": 
        !unavailableIndex.value && !_unref(chatStore).loading ? handleSend : () => {}
      
              }, null, 8, ["bot-message-title", "suggestion-categories", "handle-suggestion-click"]))
            : _createCommentVNode("", true),
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(conversation).messages, (message, index) => {
            return (_openBlock(), _createBlock(ChatMessage, {
              key: index,
              message: message,
              "bot-message-title": botMessageTitle.value,
              "bot-message-footer": props.botMessageFooter,
              "default-section": 
        (message as LegalAssistantSectionsMessage).defaultSection
      ,
              "product-index": _unref(conversation).ragIndex
            }, {
              default: _withCtx(() => [
                (message.isBot && !message.text && _unref(chatStore).loading)
                  ? (_openBlock(), _createBlock(AnimatedDots, { key: 0 }))
                  : _createCommentVNode("", true)
              ]),
              _: 2
            }, 1032, ["message", "bot-message-title", "bot-message-footer", "default-section", "product-index"]))
          }), 128)),
          (
        (_unref(chatStore).loading || loadingSuggestions.value) &&
        !_unref(conversation).messages.some((m) => m.isBot && !m.text)
      )
            ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
                _createVNode(FloatingCard, null, {
                  default: _withCtx(() => [
                    _createVNode(BotMessageHeader, { title: botMessageTitle.value }, null, 8, ["title"]),
                    _createVNode(AnimatedDots)
                  ]),
                  _: 1
                })
              ]))
            : _createCommentVNode("", true)
        ], 512)),
    (!loadingConversation.value && !unavailableIndex.value)
      ? (_openBlock(), _createBlock(ChatInputComponent, {
          key: 2,
          value: chatInputValue.value,
          onSend: handleSend,
          "allow-document-text-selection": _ctx.allowDocumentTextSelection,
          disabled: _unref(chatStore).loading || loadingSuggestions.value
        }, null, 8, ["value", "allow-document-text-selection", "disabled"]))
      : _createCommentVNode("", true)
  ], 64))
}
}

})